import React, { useState, useEffect } from "react";
import Nav from "./Navigation";
import { getTokenInternal } from "./useToken";
import LoginRequired from "./LoginRequired.js";

const FriendList = () => {
  const [allFriends, setAllFriends] = useState([]);
  const [selectedFriendId, setSelectedFriendId] = useState(null);
  const [friendItems, setFriendItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [modalImageSrc, setModalImageSrc] = useState(null);
  const [whoTrustsMeIds, setWhoTrustsMeIds] = useState([]);
  const [myClaimsByKey, setMyClaimsByKey] = useState({}); // "itemId-friendAccountId" -> claim id, from the server
  const [claimMessage, setClaimMessage] = useState("");
  const [currentUserId, setCurrentUserId] = useState(null);
  const [uncheckedUsers, setUncheckedUsers] = useState([]);
  const [tokenChecked, setTokenChecked] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    getTokenInternal().then(t => {
      setIsLoggedIn(!!t);
      setTokenChecked(true);
    });
  }, []);

  // --- Translation support ---
  const [typeTranslations, setTypeTranslations] = useState({});
  const [eventTranslations, setEventTranslations] = useState({});
  const [mapsNameTranslations, setMapsNameTranslations] = useState({});
  const [itemNameTranslations, setItemNameTranslations] = useState({});

  const translateType = (rawType) => typeTranslations[rawType] || rawType;
  const translateEvent = (rawEvent) => eventTranslations[rawEvent] || rawEvent;
  const translateMapsName = (rawMapsName) => mapsNameTranslations[rawMapsName] || rawMapsName;
  const translateItemName = (rawName) => itemNameTranslations[rawName] || rawName;

  useEffect(() => {
    const fetchTranslations = async () => {
      try {
        const token = await getTokenInternal();
        if (token?.account?.id) setCurrentUserId(token.account.id);
        let languageId = 1;
        if (token?.account?.username) {
          const accountRes = await fetch(
            `${process.env.REACT_APP_API_HOST}/api/accounts/${encodeURIComponent(token.account.username)}`
          );
          if (accountRes.ok) {
            const accountData = await accountRes.json();
            languageId = accountData.language_id || 1;
          }
        }

        const [typesRes, eventRes, mapsNameRes, itemsRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_HOST}/api/translations/lookup/types?language_id=${languageId}`),
          fetch(`${process.env.REACT_APP_API_HOST}/api/translations/lookup/event?language_id=${languageId}`),
          fetch(`${process.env.REACT_APP_API_HOST}/api/translations/lookup/maps_name?language_id=${languageId}`),
          fetch(`${process.env.REACT_APP_API_HOST}/api/translations/lookup/items?language_id=${languageId}`),
        ]);

        if (typesRes.ok) setTypeTranslations(await typesRes.json());
        if (eventRes.ok) setEventTranslations(await eventRes.json());
        if (mapsNameRes.ok) setMapsNameTranslations(await mapsNameRes.json());
        if (itemsRes.ok) setItemNameTranslations(await itemsRes.json());
      } catch (error) {
        console.error("Error fetching translations:", error.message);
      }
    };

    fetchTranslations();
  }, []);

  useEffect(() => {
    const fetchFriends = async () => {
      try {
        const token = await getTokenInternal();
        if (token) {
          const response = await fetch(`${process.env.REACT_APP_API_HOST}/api/friendsList`, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token.access_token}`,
              "Content-Type": "application/json",
            },
          });
          if (response.ok) setAllFriends(await response.json());
        }
      } catch (error) {
        console.error("Error fetching friends:", error);
      }
    };
    fetchFriends();
  }, []);

  useEffect(() => {
    const fetchWhoTrustsMe = async () => {
      try {
        const token = await getTokenInternal();
        if (!token) return;
        const response = await fetch(
          `${process.env.REACT_APP_API_HOST}/api/trusted-claimers/who-trusts-me`,
          { headers: { Authorization: `Bearer ${token.access_token}` } }
        );
        if (response.ok) {
          const data = await response.json();
          setWhoTrustsMeIds(data.map((account) => account.account_id));
        }
      } catch (error) {
        console.error("Error fetching trusted-by list:", error.message);
      }
    };
    fetchWhoTrustsMe();
    loadMyClaims();
  }, []);

  // What *I've* already claimed for others -- keyed by item+friend so the
  // "Claim" / "Claim for them" buttons can show "Cancel" instead, based on
  // real server state rather than only what happened this session.
  const loadMyClaims = async () => {
    try {
      const token = await getTokenInternal();
      if (!token) return;
      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}/api/claims/made-by-me`,
        { headers: { Authorization: `Bearer ${token.access_token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        const map = {};
        data.forEach((c) => {
          map[`${c.item_id}-${c.claimed_for_account_id}`] = c.id;
        });
        setMyClaimsByKey(map);
      }
    } catch (error) {
      console.error("Error fetching my claims:", error.message);
    }
  };

  // Fetch unchecked users whenever the selected item changes
  useEffect(() => {
    if (!selectedItem) { setUncheckedUsers([]); return; }
    fetch(`${process.env.REACT_APP_API_HOST}/items/${selectedItem.item_id}/users/unchecked`)
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => setUncheckedUsers(data))
      .catch(() => setUncheckedUsers([]));
  }, [selectedItem]);

  const resolveFriendAccountId = (friend) =>
    friend.user_id !== currentUserId ? friend.user_id : friend.friend_id;

  // Claim for any target friend (primary or other)
  const handleClaim = async (itemId, targetAccountId) => {
    setClaimMessage("");
    try {
      const token = await getTokenInternal();
      const response = await fetch(`${process.env.REACT_APP_API_HOST}/api/claims/create`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token.access_token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ claimed_for_account_id: targetAccountId, item_id: itemId }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || "Could not submit claim");
      if (targetAccountId === selectedFriendId) {
        setClaimMessage(`Claim submitted for "${data.item_name}". Your friend still needs to approve it.`);
      }
      setMyClaimsByKey((prev) => ({
        ...prev,
        [`${data.item_id}-${data.claimed_for_account_id}`]: data.id,
      }));
    } catch (error) {
      setClaimMessage(error.message);
    }
  };

  const handleCancelClaim = async (claimId, itemId, targetAccountId) => {
    setClaimMessage("");
    try {
      const token = await getTokenInternal();
      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}/api/claims/${claimId}/cancel`,
        { method: "DELETE", headers: { Authorization: `Bearer ${token.access_token}` } }
      );
      if (!response.ok) throw new Error("Could not cancel claim");
      setMyClaimsByKey((prev) => {
        const next = { ...prev };
        delete next[`${itemId}-${targetAccountId}`];
        return next;
      });
    } catch (error) {
      setClaimMessage(error.message);
    }
  };

  const handleFriendSelect = async (friendId) => {
    try {
      const token = await getTokenInternal();
      if (token) {
        const response = await fetch(`${process.env.REACT_APP_API_HOST}/users/${friendId}/items`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token.access_token}`,
            "Content-Type": "application/json",
          },
        });
        if (response.ok) {
          const items = await response.json();
          setFriendItems(items.filter((item) => item.games_id === 1 && !item.checked));
        }
      }
    } catch (error) {
      console.error("Error fetching friend's items:", error.message);
    }
  };

  const getCurrentUserId = async () => {
    try {
      const token = await getTokenInternal();
      return token ? token.account.id : null;
    } catch (error) {
      return null;
    }
  };

  const filterItemsBySearchQuery = (items) => {
    const q = searchQuery.toLowerCase();
    return items.filter((item) =>
      item.name.toLowerCase().includes(q) ||
      translateItemName(item.name).toLowerCase().includes(q)
    );
  };

  const isTrustedByFriend = whoTrustsMeIds.includes(selectedFriendId);

  const nonEventItems = friendItems.filter((item) => item.event === "Primary");
  const otherEvents = friendItems.filter((item) => item.event !== "Primary");
  const selectedEvents = selectedEvent === "__ALL__"
    ? otherEvents
    : friendItems.filter((item) => item.event === selectedEvent);

  // Username of the friend whose list we're viewing (to exclude from "others" list)
  const selectedFriendEntry = allFriends.find(
    (f) => f.user_id === selectedFriendId || f.friend_id === selectedFriendId
  );
  const selectedFriendUsername = selectedFriendEntry?.friend_username;

  const renderItemRow = (filteredItem) => {
    const isSelected = selectedItem?.user_item_id === filteredItem.user_item_id;
    return (
      <div
        key={filteredItem.user_item_id}
        className={`border p-1 rounded bg-neutral-400 flex items-center ${isSelected ? "ring-2 ring-yellow-300" : ""}`}
      >
        <div className="ml-2 flex-1 min-w-0 truncate">{translateItemName(filteredItem.name)}</div>
        <button
          className="ml-2 flex-shrink-0 bg-blue-500 border-solid border-2 border-white text-white px-1 py-0 rounded"
          onClick={() => setSelectedItem(isSelected ? null : filteredItem)}
        >
          {isSelected ? "Hide" : "Info"}
        </button>
        {isTrustedByFriend && (() => {
          const existingClaimId = myClaimsByKey[`${filteredItem.item_id}-${selectedFriendId}`];
          return existingClaimId ? (
            <button
              className="ml-2 flex-shrink-0 bg-amber-600 border-solid border-2 border-white text-white px-1 py-0 rounded"
              onClick={() => handleCancelClaim(existingClaimId, filteredItem.item_id, selectedFriendId)}
            >
              Unclaim
            </button>
          ) : (
            <button
              className="ml-2 flex-shrink-0 bg-green-600 border-solid border-2 border-white text-white px-1 py-0 rounded"
              onClick={() => handleClaim(filteredItem.item_id, selectedFriendId)}
            >
              Claim
            </button>
          );
        })()}
      </div>
    );
  };

  const renderTypeGroups = (items) => {
    const types = Array.from(new Set(filterItemsBySearchQuery(items).map((i) => i.types)));
    return (
      <div className="flex flex-wrap">
        {types.map((type) => (
          <div key={type} className="mb-6 ml-2">
            <h4 className="text-md font-bold mb-2">{translateType(type)}</h4>
            <div className="grid gap-2">
              {filterItemsBySearchQuery(items)
                .filter((i) => i.types === type)
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(renderItemRow)}
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (tokenChecked && !isLoggedIn) return <LoginRequired />;

  return (
    <div className="text-white">
      <Nav />
      <div className="container mx-auto p-4 flex flex-col sm:flex-row gap-4">

        {/* Left Column */}
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold mb-4">What Friend Needs What</h2>

          {/* Friend Selector */}
          <div className="mb-4">
            <select
              className="text-black font-bold block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:border-indigo-500 focus:ring focus:ring-indigo-200"
              onChange={async (e) => {
                const friendId = e.target.value;
                const selectedFriend = allFriends.find((f) => f.id === parseInt(friendId));
                const currentId = await getCurrentUserId();
                if (selectedFriend) {
                  const selectedId =
                    selectedFriend.user_id !== currentId
                      ? selectedFriend.user_id
                      : selectedFriend.friend_id;
                  setSelectedFriendId(selectedId);
                  setSelectedItem(null);
                  setClaimMessage("");
                  handleFriendSelect(selectedId);
                }
              }}
            >
              <option value="">Select a friend</option>
              {allFriends.length > 0
                ? allFriends
                    .sort((a, b) => a.friend_username.localeCompare(b.friend_username))
                    .map((friend) => (
                      <option key={friend.id} value={friend.id}>
                        {friend.friend_username}
                      </option>
                    ))
                : <option value="">No friends found</option>}
            </select>
          </div>

          {/* Event Dropdown */}
          <div className="mb-4">
            <label htmlFor="eventDropdown" className="text-md font-bold">Select Event:</label>
            <select
              id="eventDropdown"
              onChange={(e) => setSelectedEvent(e.target.value)}
              value={selectedEvent}
              className="ml-2 p-1 border rounded text-black"
            >
              <option value="">None</option>
              <option value="__ALL__">All Events</option>
              {Array.from(new Set(otherEvents.map((item) => item.event))).map((event) => (
                <option key={event} value={event}>{translateEvent(event)}</option>
              ))}
            </select>
            <button onClick={() => setSelectedEvent("")} className="ml-2 p-1 border rounded">Clear</button>
          </div>

          {/* Search */}
          <div className="mb-4">
            <input
              type="text"
              placeholder="Search items..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="p-1 border rounded text-black w-full max-w-xs"
            />
          </div>

          {selectedFriendId && (
            <div className="mt-4">
              <h3 className="text-xl font-bold mb-2">Friend's Needed Items:</h3>
              {isTrustedByFriend && (
                <p className="text-green-400 mb-2">This friend trusts you to claim items for them.</p>
              )}
              {claimMessage && <p className="text-yellow-300 mb-2">{claimMessage}</p>}

              {/* Event Items */}
              {selectedEvent && selectedEvents.length > 0 && (
                <div className="mb-4">
                  <p className="font-bold text-xl underline mb-2">{translateEvent(selectedEvent)}</p>
                  {renderTypeGroups(selectedEvents)}
                </div>
              )}

              {/* Primary Items */}
              {nonEventItems.length > 0 && (
                <div className="mb-4">
                  <p className="font-bold text-xl underline mb-2">{translateEvent("Primary")}</p>
                  {renderTypeGroups(nonEventItems)}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Column - Item Detail Panel */}
        <div className="w-full sm:w-[300px] flex-shrink-0">
          {selectedItem && (
            <div className="w-full p-4 bg-neutral-800 rounded-lg text-white border border-gray-500 sm:sticky sm:top-4 self-start max-h-[90vh] overflow-y-auto">
              <h2 className="text-lg font-bold mb-1">{translateItemName(selectedItem.name)}</h2>
              {translateItemName(selectedItem.name) !== selectedItem.name && (
                <p className="text-xs text-gray-400 mb-2">{selectedItem.name}</p>
              )}
              <div className="mb-3">
                Map: {translateMapsName(selectedItem.maps_name)}
                {translateMapsName(selectedItem.maps_name) !== selectedItem.maps_name && (
                  <span className="text-xs text-gray-400 ml-1">({selectedItem.maps_name})</span>
                )}
              </div>
              <img
                className="object-scale-down h-48 w-full mb-1 cursor-pointer"
                src={`${process.env.REACT_APP_R2_PUBLIC_URL}/${selectedItem.url}`}
                alt="Item"
                loading="lazy"
                onClick={() => setModalImageSrc(`${process.env.REACT_APP_R2_PUBLIC_URL}/${selectedItem.url}`)}
                onError={(e) => { e.target.src = '/gamelogos/Unofficial.PNG'; }}
              />
              <div className="text-center text-xs text-gray-400 mb-3">Click image for full size</div>

              {/* Other friends who also need this item */}
              {(() => {
                const others = allFriends.filter(
                  (f) =>
                    uncheckedUsers.includes(f.friend_username) &&
                    f.friend_username !== selectedFriendUsername
                );
                if (others.length === 0) return null;
                return (
                  <div className="border-t border-gray-600 pt-3">
                    <h3 className="font-bold text-sm mb-2">Others who need this:</h3>
                    <ul className="text-sm space-y-2">
                      {others.map((friend, idx) => {
                        const friendAccountId = resolveFriendAccountId(friend);
                        const friendTrustsMe = whoTrustsMeIds.includes(friendAccountId);
                        const claimKey = `${selectedItem.item_id}-${friendAccountId}`;
                        const existingClaimId = myClaimsByKey[claimKey];
                        return (
                          <li key={idx}>
                            <div className="flex items-center justify-between">
                              <span>{friend.friend_username}</span>
                              {friendTrustsMe && (
                                existingClaimId ? (
                                  <button
                                    className="ml-2 bg-amber-600 border-solid border-2 border-white text-white px-1 py-0 rounded text-xs"
                                    onClick={() => handleCancelClaim(existingClaimId, selectedItem.item_id, friendAccountId)}
                                  >
                                    Unclaim
                                  </button>
                                ) : (
                                  <button
                                    className="ml-2 bg-green-600 border-solid border-2 border-white text-white px-1 py-0 rounded text-xs"
                                    onClick={() => handleClaim(selectedItem.item_id, friendAccountId)}
                                  >
                                    Claim for them
                                  </button>
                                )
                              )}
                            </div>
                            <div className="text-xs text-gray-400">
                              Discord: {friend.visible ? friend.discord_name || "N/A" : "N/A"}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      </div>

      {/* Fullscreen Modal */}
      {modalImageSrc && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-75 z-50 p-4"
          onClick={() => setModalImageSrc(null)}
        >
          <img
            src={modalImageSrc}
            alt="Modal Content"
            className="max-w-[90vw] max-h-[90vh] object-contain"
          />
        </div>
      )}
    </div>
  );
};

export default FriendList;
